import { ImplementorTemplateParams } from "components/stories/video-studio/utils/creatomate/setCustomTemplate";
import syncMediaElements from "components/stories/video-studio/utils/creatomate/syncMediaElements";
import { ElementSource } from "chq";
import getText from "components/editor/getText";
import cacheAssetIfNeeded from "./utils/cacheAssetIfNeeded";
import calculateVideoDuration from "./utils/calculateVideoDuration";
import { findAllGenericElements } from "../../genericTemplate/utils/findGenericElementByName";

interface ElementUpdate {
  id?: string;
  updates: Partial<ElementSource>;
}

const genericVideoTemplate = async ({
  preview,
  mediaElements,
  organization,
  defaultVideoProperties,
  story
}: ImplementorTemplateParams) => {
  const json = preview.getSource();
  const elements = json?.elements;
  const {
    storyElement,
    captionElement,
    creatorFirstNameElement,
    creatorLastnameElement,
    creatorNameElement,
    creatorTitleElement,
    creatorDeparmentElement,
    creatorProfessionElement,
    creatorBusinessElement,
    creatorProfileImageElement,
    creatorLocationElement,
    logoElement,
    nameAndTitleElement,
    outroElement,
    promptElement,
    trendElement
  } = findAllGenericElements(elements);

  const firstStoryElement = storyElement.reduce((min, current) => (
    current.time < min.time ? current : min
  ), storyElement?.[0]);

  const lastOutroElement = outroElement.reduce((max, current) => (
    (current.time + current.duration) > (max.time + max.duration) ? current : max
  ), outroElement?.[0]);

  const newSource = await syncMediaElements(preview, mediaElements, {
    ...defaultVideoProperties,
    track: firstStoryElement?.track,
    x: firstStoryElement?.x,
    width: firstStoryElement?.width,
    height: firstStoryElement?.height,
    border_radius: firstStoryElement?.border_radius,
    animations: firstStoryElement?.animations,
    name: firstStoryElement?.name
  }, firstStoryElement?.id, (firstStoryElement?.time || 0));
  const videosDuration = calculateVideoDuration(mediaElements);

  const storyEndTime = (firstStoryElement?.time ?? 0) + (firstStoryElement?.duration ?? 0);
  const lastOutroDiff = lastOutroElement ? (lastOutroElement.time - storyEndTime) : 0;

  const relativeElements = newSource.elements.filter((element: ElementSource) => {
    const elementName = element.name || "";
    const isRelativeElement = elementName.includes("RT=");
    const isIntroElement = elementName.includes("RT=Intro");
    const isOutroElement = elementName.includes("RT=Outro");
    return isRelativeElement && !isIntroElement && !isOutroElement;
  });

  const elementUpdates: ElementUpdate[] = [
    ...logoElement.map(element => ({
      id: element.id,
      updates: {
        source: organization.logo.originalUrl,
        name: `${organization.name}'s logo`
      }
    })),
    ...nameAndTitleElement.map(element => ({
      id: element.id,
      updates: {
        text: `${story?.creator?.name}\n${story?.creator?.title || ""}`
      }
    })),
    ...creatorLocationElement.map(element => ({
      id: element.id,
      updates: {
        text: story?.creator?.location?.name
      }
    })),
    ...promptElement.map(element => ({
      id: element.id,
      updates: {
        text: story?.question?.question
      }
    })),
    ...trendElement.map(element => ({
      id: element.id,
      updates: {
        text: story?.question?.parentStoryQuestion?.question
      }
    })),
    ...captionElement.map(element => ({
      id: element.id,
      updates: {
        text: getText(story?.body)
      }
    })),
    ...creatorFirstNameElement.map(element => ({
      id: element.id,
      updates: {
        text: story?.creator?.firstName || story?.creator?.name
      }
    })),
    ...creatorLastnameElement.map(element => ({
      id: element.id,
      updates: {
        text: story?.creator?.lastName || story?.creator?.name
      }
    })),
    ...creatorNameElement.map(element => ({
      id: element.id,
      updates: {
        text: story?.creator?.name
      }
    })),
    ...creatorTitleElement.map(element => ({
      id: element.id,
      updates: {
        text: story?.creator?.title
      }
    })),
    ...creatorDeparmentElement.map(element => ({
      id: element.id,
      updates: {
        text: story?.creator?.departments?.[0]?.name
      }
    })),
    ...creatorProfessionElement.map(element => ({
      id: element.id,
      updates: {
        text: story?.creator?.departments?.[0]?.name
      }
    })),
    ...creatorBusinessElement.map(element => ({
      id: element.id,
      updates: {
        text: story?.creator?.departments?.[0]?.name
      }
    })),
    ...creatorProfileImageElement.map(element => ({
      id: element.id,
      updates: {
        source: story?.creator?.avatar?.thumbUrl,
        name: `${story?.creator?.name}'s photo`
      }
    })),
    ...outroElement.map(element => {
      const outroDiff = (element?.time ?? 0) - storyEndTime;
      return {
        id: element.id,
        updates: {
          time: (firstStoryElement?.time || 0) + videosDuration + outroDiff
        }
      };
    }),
    ...relativeElements.map((element: ElementSource) => {
      const relativeElement = newSource.elements.find((existingElement: ElementSource) => (
        element.name.includes(existingElement.id)
      ));

      if (!relativeElement) return {};
      const relativeElementEndTime = (relativeElement.time ?? 0) + (relativeElement.duration ?? 0);
      const elementEndTime = (element.time ?? 0) + (element.duration ?? 0);
      if (elementEndTime > relativeElementEndTime) {
        return {
          id: element.id,
          updates: {
            duration: relativeElementEndTime - (element.time ?? 0)
          }
        };
      }

      return {};
    }).filter((update: ElementUpdate) => Object.keys(update).length > 0)
  ];

  const elementMappings = elementUpdates
    .filter((update): update is ElementUpdate & { id: string } => Boolean(update.id))
    .reduce((acc, { id, updates }) => {
      acc[id] = { ...acc[id], ...updates };
      return acc;
    }, {} as Record<string, Partial<ElementSource>>);

  const totalDuration = (firstStoryElement?.time ?? 0)
    + videosDuration + ((lastOutroElement?.duration ?? 0) + lastOutroDiff);
  await preview.setSource({
    ...newSource,
    elements: newSource.elements.map((element: ElementSource) => ({
      ...element,
      ...elementMappings[element.id]
    })),
    duration: totalDuration
  });
};

export default genericVideoTemplate;
